import { DateTime } from "luxon";

import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import { ScheduleResourceResponse } from "app/utils/request/scheduling/facilityResources";
import { defaultRateForBay } from "app/routes/Calendar/EventForm/resourceChoice";
import { CUSTOM_SESSION_OPTION } from "app/routes/Calendar/EventForm/customSession";

interface SelectionDefaultsParams {
  /** ISO start of the range FullCalendar handed back from `select`. */
  startStr: string;
  /** ISO end of that range. */
  endStr: string;
  timeZone: string;
  /** Booking options offered for the clicked column (facility- or coach-level). */
  lessonRates?: LessonRateModel[];
  /** Length of one snap interval, i.e. what a plain click selects. */
  snapInMinutes: number;
  /** The bay of a click on a bay column. Absent for a coach column. */
  bayId?: number | null;
  /** The resources of the facility, to find the section of the bay. */
  resources?: ScheduleResourceResponse[];
}

interface SelectionDefaults {
  /** ISO end the new event should open with. */
  end: string;
  /** Booking option the range represents, or null for a custom session. */
  lessonRate: LessonRateModel | null;
  /**
   * The Session Type value the form opens with. A click on a bay gives null
   * for a custom lesson, the value of the "Custom" entry. A click on a coach
   * column with no option keeps the value it had before (undefined).
   */
  lessonRateId: number | null | undefined;
}

/**
 * The option a new lesson starts with. A click on a coach column takes the
 * default option. A click on a bay column takes the option for the bay
 * (see `defaultRateForBay`), or null for a custom lesson with the bay.
 */
function startingRate(
  lessonRates: LessonRateModel[] | undefined,
  bayId: number | null | undefined,
  resources: ScheduleResourceResponse[] | undefined,
): LessonRateModel | null {
  if (bayId == null) {
    return lessonRates?.find((lr) => lr.defaultRate) || null;
  }

  const bay = (resources ?? []).find((resource) => resource.id === bayId);
  return defaultRateForBay(lessonRates, bay);
}

/**
 * Work out what a calendar selection means for a new session (CN-2797).
 *
 * A plain click selects a single snap interval — the facility's scheduling
 * interval, which has nothing to do with how long its booking options run. Left
 * alone, that opened a 30-minute session carrying a 60- or 90-minute booking
 * option, and the mismatch was only corrected if the coach re-picked the option
 * in the form. So a click is stretched to the default option's duration, while a
 * deliberate drag keeps the range it drew and carries the default option only
 * when it matches that duration — otherwise it is a custom session, with no
 * option, rate or location prefilled.
 *
 * On a bay column, the option that `startingRate` gives takes the place of
 * the default option.
 */
export function resolveSelectionDefaults({
  startStr,
  endStr,
  timeZone,
  lessonRates,
  snapInMinutes,
  bayId,
  resources,
}: SelectionDefaultsParams): SelectionDefaults {
  const onBay = bayId != null;
  const defaultRate = startingRate(lessonRates, bayId, resources);
  // A bay that no option includes opens a custom lesson with the bay. It takes
  // the length that the "Custom" entry gives when a user picks it by hand.
  const customOnBay = onBay && !defaultRate;
  // Use the backend-parsed minute count, not parseInt(duration): a free-form
  // duration like "2 hours" would parse to 2, computing a 2-minute session.
  const duration = customOnBay
    ? CUSTOM_SESSION_OPTION.durationInMinutes
    : defaultRate?.durationInMinutes;
  const withRate = (lessonRate: LessonRateModel | null) => ({
    lessonRate,
    lessonRateId: lessonRate?.id ?? (onBay ? null : undefined),
  });
  const start = DateTime.fromISO(startStr, { zone: timeZone });
  const end = DateTime.fromISO(endStr, { zone: timeZone });

  // Nothing to measure the selection against: leave the range as drawn.
  if (
    typeof duration !== "number" ||
    duration <= 0 ||
    !start.isValid ||
    !end.isValid
  ) {
    return { end: endStr, ...withRate(defaultRate) };
  }

  const selectedMinutes = end.diff(start, "minutes").minutes;

  if (selectedMinutes <= snapInMinutes) {
    return {
      end: start.plus({ minutes: duration }).toISO(),
      ...withRate(defaultRate),
    };
  }

  return {
    end: endStr,
    ...withRate(selectedMinutes === duration ? defaultRate : null),
  };
}

export default resolveSelectionDefaults;
